import { GAME } from '@/config/game';
import { SPAWNS } from '@/config/waves';
import { MOUNTS_BY_TIER } from '@/config/weapons';
import type { Boss } from '@/bosses/boss-base';
import { BOSS_POOL, bossById } from '@/bosses/registry';
import type { Player } from '@/entities/player';
import { wireBossHooks } from '@/combat/boss-hooks';
import type { World } from '@/scene/world';
import type { RunState } from './run-state';
import { difficultyFor, finaleHpMul } from './sector-hooks';

/**
 * Spawn the room's boss. Death retry: the run is NOT over — the same boss
 * returns at the same level (docs/12 G1). Otherwise the pre-picked fight
 * (shop preview, B5) or a fresh draw at the run depth.
 */
export function spawnRoomBoss(
  world: World, run: RunState, retry: boolean, spawn: { x: number; y: number },
): Boss {
  let entry = run.currentFight && retry ? bossById(run.currentFight.id) : undefined;
  let level: number;
  if (entry && run.currentFight) {
    level = run.currentFight.level;
  } else {
    const pick = run.nextPick ?? run.drawFight(run.depth);
    run.nextPick = null;
    entry = bossById(pick.id) ?? BOSS_POOL[0]!;
    level = pick.level;
  }
  run.currentFight = { id: entry.def.id, level };
  // Boss spot in the FIGHT piece (the chamber of an advance room)
  const S = SPAWNS.boss;
  const arena = world.arena;
  const at = arena.clearPoint(S.at[0], S.at[1], spawn.x, spawn.y, S.away, S.clear, arena.fight);
  const boss = entry.create(level, at.x, at.y, at.x, at.y);
  boss.setArena(world.arena.width, world.arena.height);
  boss.scaleHp(firepowerHpMul(world.player) * difficultyFor(run).enemyHp * finaleHpMul(run));
  wireBossHooks(boss, world, () => run.rng);
  world.mountBoss(boss);
  return boss;
}

/** Firepower-aware HP: a stacked build stops one-shotting bosses (docs/12 D4). */
function firepowerHpMul(player: Player): number {
  const st = player.stats;
  const weapon = player.weapon;
  const mounts = MOUNTS_BY_TIER[Math.min(weapon.gunTier, MOUNTS_BY_TIER.length) - 1]!.length;
  const run = GAME.run;
  const power = st.damageMul * st.fireRateMul *
    st.catDamage[weapon.category] *
    st.catFireRate[weapon.category] *
    mounts * (1 + run.bossHpPerExtraWeapon * (weapon.equipped.length - 1));
  return Math.min(run.bossHpPowerCap, 1 + run.bossHpPowerScale * (power - 1));
}
